import 'bootstrap-icons/font/bootstrap-icons.css';
import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useLogs } from '../context/LogContext';
import '../css/ProductTypes.css';
import useIdleLogout from "../hooks/useIdleLogout";
const API_BASE = process.env.REACT_APP_PRODUCTTYPES_API_URL; // e.g. http://103.7.253.31/merchant/api/admin/business-types
const MART_CATEGORY_API_BASE = process.env.REACT_APP_MART_CATEGORIES_API_URL || ''; // e.g. http://103.7.253.31/merchant/api/category/mart

// Use specific file base for merchant like rides page
const FILE_BASE_MERCHANT = (
  process.env.REACT_APP_MERCHANT_FILE_BASE_URL ||
  process.env.REACT_APP_FILE_BASE_URL_MERCHANT || // legacy name fallback
  process.env.REACT_APP_FILE_BASE_URL ||
  ''
).replace(/\/+$/, '');

const API_ORIGIN = (() => {
  try { return new URL(API_BASE || MART_CATEGORY_API_BASE).origin; } catch { return ''; }
})();

// Only use the merchant base (keeps '/merchant' path). If not set, fall back to origin.
const FILE_BASE_FOR_PATHS = FILE_BASE_MERCHANT || API_ORIGIN || '';

const PLACEHOLDER_IMG = '/assets/img/placeholder.png';

// Simple URL converter like RideTypes page
const toAbsUrl = (val) => {
  if (!val) return '';
  if (/^(data:|blob:|https?:\/\/)/i.test(val)) return val; // already absolute
  const path = val.startsWith('/') ? val : `/${val}`;
  return FILE_BASE_FOR_PATHS ? `${FILE_BASE_FOR_PATHS}${path}` : path;
};

// Stable image component to prevent flickering
const StableImage = ({ src, alt, className, onError }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImgSrc(src);
    setLoading(true);
  }, [src]);

  const handleError = (e) => {
    setImgSrc(PLACEHOLDER_IMG);
    setLoading(false);
    onError?.(e);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  return (
    <div className={`image-container ${loading ? 'loading' : ''}`}>
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
        style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s ease' }}
      />
      {loading && <div className="image-placeholder">Loading...</div>}
    </div>
  );
};

function getAdminMeta() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      user_id:    u?.user_id ?? u?.id ?? 1,
      admin_name: u?.user_name ?? 'Admin',
    };
  } catch {
    return { user_id: 1, admin_name: 'Admin' };
  }
}

export default function ProductTypes() {
    useIdleLogout();
  const { logActivity } = useLogs();

  // refs to clear file inputs after submit
  const newProductImageRef = useRef(null);
  const newMartCategoryImageRef = useRef(null);

  /* ===================== Product Types (mart) ===================== */
  // form (create)
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile]     = useState(null);

  // status
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState(null);
  const [fadeOut, setFadeOut]   = useState(false);

  // table
  const [rows, setRows]               = useState([]); // mart business types
  const [editingId, setEditingId]     = useState(null);
  const [editRowData, setEditRowData] = useState({}); // { name, description, preview, imgFile }
  const [originalRowData, setOriginalRowData] = useState({});

  /* ===================== Mart Categories ===================== */
  const [categories, setCategories] = useState([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState(''); // id (from rows)
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryData, setEditCategoryData] = useState({}); // { name, description, business_type_id, imgFile, preview }
  const [originalCategoryData, setOriginalCategoryData] = useState({});

  /* ---------------- helpers ---------------- */
  const normalizeList = (raw) => {
    const list =
      Array.isArray(raw) ? raw :
      raw?.data ?? raw?.items ?? raw?.results ??
      raw?.business_types ?? raw?.businessTypes ?? [];

    return list
      .map(it => {
        const rawImg =
          it.image_url ?? it.imageUrl ?? it.image_path ?? it.imagePath ??
          it.image ?? it.icon ?? it.photo ?? it.picture ?? it.logo ?? it.img;
        return {
          ...it,
          id:   it.id || it._id || it.business_type_id || it.product_type_id || it.type_id,
          name: it.name ?? it.type_name ?? '',
          description: it.description ?? it.note ?? '',
          types: it.types ?? it.category ?? '',
          imgUrl: toAbsUrl(rawImg),
        };
      })
      .filter(it => String(it.types || '').toLowerCase() === 'mart');
  };

  const normalizeCategories = (raw) => {
    const list =
      Array.isArray(raw) ? raw :
      raw?.data ?? raw?.items ?? raw?.results ??
      raw?.categories ?? raw?.martCategories ?? [];

    return list.map(it => {
      const rawImg =
        it.category_image ??
        it.image_url ?? it.imageUrl ?? it.imageURL ??
        it.image_path ?? it.imagePath ??
        it.logo_url ?? it.logoUrl ??
        it.photo_url ?? it.photoUrl ??
        it.picture ?? it.icon ?? it.logo ??
        it.file_url ?? it.url ??
        it.image ?? it.path ?? '';
      return {
        ...it,
        id: it.id || it._id || it.category_id || it.mart_category_id,
        name: it.name ?? it.category_name ?? '',
        description: it.description ?? it.note ?? '',
        business_type: it.business_type ?? it.business_type_name ?? '', // keep BT name
        imgUrl: toAbsUrl(rawImg),
      };
    });
  };

  const fileToDataURL = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const fetchAll = async () => {
    try {
      const res = await fetch(`${API_BASE}`, { method: 'GET' });
      if (!res.ok) throw new Error('Failed to fetch product types');
      const data = await res.json();
      setRows(normalizeList(data));
    } catch (e) {
      console.error(e);
      setRows([]);
    }
  };

  const fetchAllCategories = async () => {
    try {
      if (!MART_CATEGORY_API_BASE) return;
      const res = await fetch(`${MART_CATEGORY_API_BASE}`, { method: 'GET' });
      if (!res.ok) throw new Error('Failed to fetch mart categories');
      const data = await res.json();
      setCategories(normalizeCategories(data));
    } catch (e) {
      console.error(e);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchAllCategories();
    // eslint-disable-next-line
  }, []);

  // flash message
  useEffect(() => {
    if (message) {
      setFadeOut(false);
      const t1 = setTimeout(() => setFadeOut(true), 1500);
      const t2 = setTimeout(() => setMessage(null), 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [message]);

  /* ---------------- Product Types: create handlers ---------------- */
  const onNewImageChange = (e) => {
    const f = e.target.files?.[0] || null;
    setImageFile(f);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const { user_id, admin_name } = getAdminMeta();

    if (rows.find(r => (r.name || '').trim().toLowerCase() === name.trim().toLowerCase())) {
      Swal.fire('Duplicate Name','That product type already exists.','error');
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      let res;
      if (imageFile) {
        const fd = new FormData();
        fd.append('name', name.trim());
        fd.append('description', (description || '').trim());
        fd.append('types', 'mart');
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', imageFile);
        res = await fetch(API_BASE, { method: 'POST', body: fd });
      } else {
        const payload = {
          name: name.trim(),
          description: (description || '').trim(),
          types: 'mart',
          user_id, admin_name,
        };
        res = await fetch(API_BASE, {
          method:'POST',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Added product type "${name.trim()}"`);
      setMessage({type:'success', text:'Product Type added successfully!'});
      // reset + clear input filename
      setName(''); setDescription('');
      setImageFile(null);
      if (newProductImageRef.current) newProductImageRef.current.value = '';
      fetchAll();
    } catch (err) {
      console.error(err);
      setMessage({type:'error', text:'Something went wrong.'});
    } finally { setLoading(false); }
  };

  /* ---------------- Product Types: table edit handlers ---------------- */
  const startRowEdit = (row) => {
    setEditingId(row.id);
    const initial = {
      name: String(row.name ?? ''),
      description: String(row.description ?? ''),
      preview: row.imgUrl || ''
    };
    setEditRowData(initial);
    setOriginalRowData(initial);
  };

  const handleRowChange = (field, value) => {
    setEditRowData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditImageChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const preview = await fileToDataURL(f);
    setEditRowData(prev => ({ ...prev, imgFile: f, preview }));
  };

  const saveRowEdit = async (id) => {
    const { user_id, admin_name } = getAdminMeta();
    const parsed = {
      name: (editRowData.name ?? '').trim(),
      description: (editRowData.description ?? '').trim(),
      types: 'mart',
      user_id,
      admin_name,
    };

    if (rows.find(r => r.id !== id && (r.name || '').trim().toLowerCase() === parsed.name.toLowerCase())) {
      Swal.fire('Duplicate','That product type already exists.','error');
      return;
    }

    try {
      setLoading(true);

      let res;
      if (editRowData.imgFile) {
        const fd = new FormData();
        Object.entries(parsed).forEach(([k,v]) => fd.append(k, String(v)));
        fd.append('image', editRowData.imgFile);
        res = await fetch(`${API_BASE}/${id}`, { method:'PUT', body: fd });
      } else {
        res = await fetch(`${API_BASE}/${id}`, {
          method:'PUT',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify(parsed),
        });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Updated product type "${parsed.name}"`);
      Swal.fire('Updated!','Product type updated successfully!','success');
      setEditingId(null); setEditRowData({}); setOriginalRowData({});
      fetchAll();
    } catch (err) {
      console.error(err);
      Swal.fire('Error','Could not update product type.','error');
    } finally { setLoading(false); }
  };

  const cancelRowEdit = () => {
    setEditRowData(originalRowData);
    setEditingId(null);
    setOriginalRowData({});
  };

  const handleDelete = async (id) => {
    const { user_id, admin_name } = getAdminMeta();
    const nameToDelete = rows.find(r => r.id === id)?.name;

    const { isConfirmed } = await Swal.fire({
      title:'Are you sure?', text:'This cannot be undone.', icon:'warning',
      showCancelButton:true, confirmButtonText:'Yes, delete',
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method:'DELETE',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ user_id, admin_name }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Deleted product type "${nameToDelete}"`);
      Swal.fire('Deleted!','Product type removed.','success');
      fetchAll();
    } catch (err) {
      console.error(err);
      Swal.fire('Error','Could not delete product type.','error');
    }
  };

  /* ===================== Mart Categories: handlers ===================== */
  const onNewCategoryImageChange = async (e) => {
    const f = e.target.files?.[0];
    setCategoryImageFile(f || null);
  };

  const handleCategorySubmit = async e => {
    e.preventDefault();
    if (!MART_CATEGORY_API_BASE) {
      Swal.fire('Configuration Error','REACT_APP_MART_CATEGORIES_API_URL is not set.','error');
      return;
    }

    const { user_id, admin_name } = getAdminMeta();

    if (!selectedBusinessType) {
      Swal.fire('Required', 'Please select a product business type', 'error');
      return;
    }

    if (categories.find(c => (c.name || '').trim().toLowerCase() === categoryName.trim().toLowerCase())) {
      Swal.fire('Duplicate Name','That mart category already exists.','error');
      return;
    }

    const businessTypeName = rows.find(r => String(r.id) === String(selectedBusinessType))?.name || '';

    try {
      setLoading(true); setMessage(null);

      let res;
      if (categoryImageFile) {
        const fd = new FormData();
        fd.append('category_name', categoryName.trim());
        fd.append('description', (categoryDescription || '').trim());
        fd.append('business_type', businessTypeName);
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', categoryImageFile);
        res = await fetch(MART_CATEGORY_API_BASE, { method: 'POST', body: fd });
      } else {
        const payload = {
          category_name: categoryName.trim(),
          description: (categoryDescription || '').trim(),
          business_type: businessTypeName,
          user_id,
          admin_name,
        };
        res = await fetch(MART_CATEGORY_API_BASE, {
          method:'POST',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Added mart category "${categoryName.trim()}" under business type "${businessTypeName}"`);
      setMessage({type:'success', text:'Mart Category added successfully!'});
      // reset + clear input filename
      setCategoryName(''); setCategoryDescription('');
      setCategoryImageFile(null);
      setSelectedBusinessType('');
      if (newMartCategoryImageRef.current) newMartCategoryImageRef.current.value = '';
      fetchAllCategories();
    } catch (err) {
      console.error(err);
      setMessage({type:'error', text:'Something went wrong.'});
    } finally { setLoading(false); }
  };

  // Fallback helper for possible server typo /amrt on UPDATE only
  const putWithMartFallback = async (url, options) => {
    const res = await fetch(url, options);
    if (res?.status === 404 && url.includes('/category/mart/')) {
      const alt = url.replace('/category/mart/', '/category/amrt/');
      return fetch(alt, options);
    }
    return res;
  };

  const startCategoryEdit = (category) => {
    setEditingCategoryId(category.id);

    // resolve currently selected business type id from the category's business_type name
    const resolvedBT = rows.find(r =>
      (r.name || '').toLowerCase() === String(category.business_type || '').toLowerCase()
    );
    const business_type_id = resolvedBT?.id ? String(resolvedBT.id) : '';

    const initial = {
      name: String(category.name ?? ''),
      description: String(category.description ?? ''),
      business_type_id,
      preview: category.imgUrl || '',
    };
    setEditCategoryData(initial);
    setOriginalCategoryData(initial);
  };

  const handleCategoryChange = (field, value) => {
    setEditCategoryData(prev => ({ ...prev, [field]: value }));
  };

  const onEditCategoryImageChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setEditCategoryData(prev => ({ ...prev, imgFile: null, preview: originalCategoryData.preview || '' }));
      return;
    }
    const preview = await fileToDataURL(f);
    setEditCategoryData(prev => ({ ...prev, imgFile: f, preview }));
  };

  const saveCategoryEdit = async (id) => {
    const { user_id, admin_name } = getAdminMeta();
    const parsed = {
      name: (editCategoryData.name ?? '').trim(),
      description: (editCategoryData.description ?? '').trim(),
      business_type_id: (editCategoryData.business_type_id ?? '').trim(),
      user_id,
      admin_name,
    };

    if (!parsed.business_type_id) {
      Swal.fire('Required', 'Please select a product business type', 'error');
      return;
    }

    if (categories.find(c => c.id !== id && (c.name || '').trim().toLowerCase() === parsed.name.toLowerCase())) {
      Swal.fire('Duplicate','That mart category already exists.','error');
      return;
    }

    const businessTypeName = rows.find(r => String(r.id) === String(parsed.business_type_id))?.name || '';

    try {
      setLoading(true);

      let res;
      if (editCategoryData.imgFile) {
        const fd = new FormData();
        fd.append('category_name', parsed.name);
        fd.append('description', parsed.description);
        fd.append('business_type', businessTypeName);
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', editCategoryData.imgFile);

        res = await putWithMartFallback(`${MART_CATEGORY_API_BASE}/${id}`, { method: 'PUT', body: fd });
      } else {
        const payload = {
          category_name: parsed.name,
          description: parsed.description,
          business_type: businessTypeName,
          user_id,
          admin_name,
        };
        res = await putWithMartFallback(`${MART_CATEGORY_API_BASE}/${id}`, {
          method:'PUT',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Updated mart category "${parsed.name}" under business type "${businessTypeName}"`);
      Swal.fire('Updated!','Mart category updated successfully!','success');
      setEditingCategoryId(null); setEditCategoryData({}); setOriginalCategoryData({});
      fetchAllCategories();
    } catch (err) {
      console.error(err);
      Swal.fire('Error','Could not update mart category.','error');
    } finally { setLoading(false); }
  };

  const cancelCategoryEdit = () => {
    setEditCategoryData(originalCategoryData);
    setEditingCategoryId(null);
    setOriginalCategoryData({});
  };

  const handleCategoryDelete = async (id) => {
    const { user_id, admin_name } = getAdminMeta();
    const nameToDelete = categories.find(c => c.id === id)?.name;

    const { isConfirmed } = await Swal.fire({
      title:'Are you sure?', text:'This cannot be undone.', icon:'warning',
      showCancelButton:true, confirmButtonText:'Yes, delete',
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${MART_CATEGORY_API_BASE}/${id}`, {
        method:'DELETE',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ user_id, admin_name }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Deleted mart category "${nameToDelete}"`);
      Swal.fire('Deleted!','Mart category removed.','success');
      fetchAllCategories();
    } catch (err) {
      console.error(err);
      Swal.fire('Error','Could not delete mart category.','error');
    }
  };

  /* ===================== RENDER ===================== */
  return (
    <>
      {/* Add Product Type */}
      <div className="card mb-4">
        <div className="card-body">
          {message && (
            <div className={`alert alert-${message.type} ${fadeOut ? 'fade-out' : ''}`}>
              {message.text}
            </div>
          )}
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Product Business Type Name:</label>
              <input
                type="text" className="form-control" placeholder="Enter product business type"
                value={name} onChange={e => setName(e.target.value)} required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Description:</label>
              <input
                type="text" className="form-control" placeholder="Enter short description"
                value={description} onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Image:</label>
              <input
                ref={newProductImageRef}
                type="file" accept="image/*" className="form-control file-pill"
                onChange={onNewImageChange}
              />
            </div>
            <div className="col-12">
              <button type="submit" className="add-type-btn" disabled={loading}>
                {loading ? 'Saving…' : 'Add Product Business Type'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Product Types Table */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">All Product Business Types</h5>
        </div>
        <div className="card-body table-responsive product-types-table-card">
          {rows.length === 0 ? (
            <p className="text-muted">No product types added yet.</p>
          ) : (
            <table className="table table-bordered align-middle">
              <colgroup>
                <col style={{ width: '80px' }} />
                <col style={{ width: '90px' }} />
                <col />
                <col />
                <col style={{ width: '160px' }} />
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th>Sl.No</th>
                  <th>Image</th>
                  <th>Product Business Type</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>

                    {/* Image (thumb + Change pill when editing) */}
                    <td className="text-center">
                      {editingId === row.id ? (
                        <div className="image-picker">
                          <StableImage
                            src={editRowData.preview || PLACEHOLDER_IMG}
                            alt=""
                            className="thumb"
                          />
                          <label className="ghost-btn mb-0">
                            Change
                            <input type="file" accept="image/*" hidden onChange={handleEditImageChange} />
                          </label>
                        </div>
                      ) : (
                        <StableImage
                          src={row.imgUrl || PLACEHOLDER_IMG}
                          alt=""
                          className="thumb"
                        />
                      )}
                    </td>

                    {/* Name */}
                    <td>
                      {editingId === row.id ? (
                        <input
                          type="text" className="form-control form-control-sm"
                          value={editRowData.name}
                          onChange={e => handleRowChange('name', e.target.value)}
                        />
                      ) : (row.name)}
                    </td>

                    {/* Description */}
                    <td>
                      {editingId === row.id ? (
                        <input
                          type="text" className="form-control form-control-sm"
                          value={editRowData.description}
                          onChange={e => handleRowChange('description', e.target.value)}
                        />
                      ) : (row.description || <span className="text-muted">—</span>)}
                    </td>

                    {/* Actions */}
                    <td className="actions-cell">
                      {editingId === row.id ? (
                        <>
                          <button
                            type="button"
                            className="btn-confirm me-2"
                            title="Save"
                            onClick={() => saveRowEdit(row.id)}
                          >
                            <i className="bi bi-check-lg" />
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            title="Cancel"
                            onClick={cancelRowEdit}
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn-view me-2"
                            title="Edit"
                            onClick={() => startRowEdit(row)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="btn-delete"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ===================== Mart - Product Category ===================== */}
      <h2 className="mb-3">Mart - Product Category</h2>

      {/* Add Mart Category */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleCategorySubmit} className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Product Business Type:</label>
              <select
                className="form-select"
                value={selectedBusinessType}
                onChange={e => setSelectedBusinessType(e.target.value)}
                required
              >
                <option value="">Select Business Type</option>
                {rows.map(row => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Category Name:</label>
              <input
                type="text" className="form-control" placeholder="Enter category name"
                value={categoryName} onChange={e => setCategoryName(e.target.value)} required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Description:</label>
              <input
                type="text" className="form-control" placeholder="Enter short description"
                value={categoryDescription} onChange={e => setCategoryDescription(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Image:</label>
              <input
                ref={newMartCategoryImageRef}
                type="file" accept="image/*" className="form-control"
                onChange={onNewCategoryImageChange}
              />
            </div>

            <div className="col-12">
              <button type="submit" className="add-type-btn" disabled={loading}>
                {loading ? 'Saving…' : 'Add Mart Category'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Mart Categories Table */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">All Mart Categories</h5>
        </div>
        <div className="card-body table-responsive product-types-table-card">
          {categories.length === 0 ? (
            <p className="text-muted">No mart categories added yet.</p>
          ) : (
            <table className="table table-bordered align-middle">
              <colgroup>
                <col style={{ width: '80px' }} />   {/* Sl.No */}
                <col style={{ width: '90px' }} />   {/* Image */}
                <col style={{ width: '220px' }} />  {/* Product Business Type */}
                <col />                              {/* Category */}
                <col />                              {/* Description */}
                <col style={{ width: '160px' }} />   {/* Actions */}
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th>SI.No</th>
                  <th>Image</th>
                  <th>Product Business Type</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, idx) => (
                  <tr key={category.id ?? `${category.name}-${idx}`}>
                    <td>{idx + 1}</td>

                    {/* Image */}
                    <td className="text-center">
                      {editingCategoryId === category.id ? (
                        <div className="image-picker">
                          <StableImage
                            src={editCategoryData.preview || PLACEHOLDER_IMG}
                            alt=""
                            className="thumb"
                          />
                          <label className="ghost-btn mb-0">
                            Change
                            <input
                              type="file" accept="image/*" hidden onChange={onEditCategoryImageChange}
                            />
                          </label>
                        </div>
                      ) : (
                        <StableImage
                          src={category.imgUrl || PLACEHOLDER_IMG}
                          alt=""
                          className="thumb"
                        />
                      )}
                    </td>

                    {/* Product Business Type */}
                    <td>
                      {editingCategoryId === category.id ? (
                        <select
                          className="form-select form-select-sm"
                          value={editCategoryData.business_type_id || ''}
                          onChange={e => handleCategoryChange('business_type_id', e.target.value)}
                          required
                        >
                          <option value="">Select Business Type</option>
                          {rows.map(bt => (
                            <option key={bt.id} value={bt.id}>{bt.name}</option>
                          ))}
                        </select>
                      ) : (
                        category.business_type || <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Name */}
                    <td>
                      {editingCategoryId === category.id ? (
                        <input
                          type="text" className="form-control form-control-sm"
                          value={editCategoryData.name}
                          onChange={e => handleCategoryChange('name', e.target.value)}
                        />
                      ) : (category.name)}
                    </td>

                    {/* Description */}
                    <td>
                      {editingCategoryId === category.id ? (
                        <input
                          type="text" className="form-control form-control-sm"
                          value={editCategoryData.description}
                          onChange={e => handleCategoryChange('description', e.target.value)}
                        />
                      ) : (category.description || <span className="text-muted">—</span>)}
                    </td>

                    {/* Actions */}
                    <td className="actions-cell">
                      {editingCategoryId === category.id ? (
                        <>
                          <button className="btn-confirm me-2" title="Save" onClick={() => saveCategoryEdit(category.id)}>
                            <i className="bi bi-check-lg" />
                          </button>
                          <button className="btn-cancel" title="Cancel" onClick={cancelCategoryEdit}>
                            <i className="bi bi-x-lg" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn-view me-2" title="Edit" onClick={() => startCategoryEdit(category)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn-delete" title="Delete" onClick={() => handleCategoryDelete(category.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>© {new Date().getFullYear()}  <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}